import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { ConsultationDTOSchema, CreateConsultationSchema } from "@arna-med/shared";
import { useBackendAuthenticated } from "@/hooks/useBackendAuthenticated";

const ConsultationsResponseSchema = z.object({
  success: z.boolean(),
  data: z.array(ConsultationDTOSchema),
  total: z.number(),
});

const ConsultationResponseSchema = z.object({
  success: z.boolean(),
  data: ConsultationDTOSchema,
});

export interface ConsultationFilters {
  patientName?: string;
  patientPersonalNumber?: string;
  dateFrom?: string;
  dateTo?: string;
  templateId?: string;
}

export function useConsultations({
  page,
  filters = {},
  limit = 10,
}: {
  page: number;
  filters?: ConsultationFilters;
  limit?: number;
}) {
  const backend = useBackendAuthenticated();
  return useQuery({
    queryKey: ["consultations", page, filters],
    queryFn: async () => {
      const params: Record<string, string | number> = { page, limit };
      if (filters.patientName) params.patientName = filters.patientName;
      if (filters.patientPersonalNumber)
        params.patientPersonalNumber = filters.patientPersonalNumber;
      if (filters.dateFrom) params.dateFrom = filters.dateFrom;
      if (filters.dateTo) params.dateTo = filters.dateTo;
      if (filters.templateId) params.templateId = filters.templateId;
      const json = await backend({
        method: "GET",
        url: "/api/v1/consultations",
        params,
      });
      return ConsultationsResponseSchema.parse(json);
    },
  });
}

export function useConsultation(id: string | undefined) {
  const backend = useBackendAuthenticated();
  return useQuery({
    queryKey: ["consultation", id],
    queryFn: async () => {
      const json = await backend({
        method: "GET",
        url: `/api/v1/consultations/${id}`,
      });
      return ConsultationResponseSchema.parse(json).data;
    },
    enabled: !!id,
  });
}

export function usePatientConsultations({
  patientId,
  page,
  limit = 5,
}: {
  patientId: string | undefined;
  page: number;
  limit?: number;
}) {
  const backend = useBackendAuthenticated();
  return useQuery({
    queryKey: ["patient-history", patientId, page],
    queryFn: async () => {
      const json = await backend({
        method: "GET",
        url: `/api/v1/consultations?patientId=${patientId!}&limit=${limit}&page=${page}`,
      });
      return ConsultationsResponseSchema.parse(json);
    },
    enabled: !!patientId,
  });
}

export function usePatientDetailConsultations({
  patientId,
  page,
  limit = 10,
}: {
  patientId: string | undefined;
  page: number;
  limit?: number;
}) {
  const backend = useBackendAuthenticated();
  return useQuery({
    queryKey: ["patient-consultations", patientId, page],
    queryFn: async () => {
      const json = await backend({
        method: "GET",
        url: "/api/v1/consultations",
        params: { patientId, page, limit },
      });
      return ConsultationsResponseSchema.parse(json);
    },
    enabled: !!patientId,
  });
}

export function usePortalConsultations({
  page,
  limit = 10,
}: {
  page: number;
  limit?: number;
}) {
  const backend = useBackendAuthenticated();
  return useQuery({
    queryKey: ["portal-consultations", page],
    queryFn: async () => {
      const json = await backend({
        method: "GET",
        url: "/api/v1/consultations",
        params: { page, limit },
      });
      return ConsultationsResponseSchema.parse(json);
    },
  });
}

export function useCreateConsultation() {
  const backend = useBackendAuthenticated();
  return useMutation({
    mutationFn: (data: z.infer<typeof CreateConsultationSchema>) =>
      backend<{ success: boolean; data: { id: string } }>({
        method: "POST",
        url: "/api/v1/consultations",
        data,
      }),
  });
}

export function useDeleteConsultation() {
  const backend = useBackendAuthenticated();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      backend({ method: "DELETE", url: `/api/v1/consultations/${id}` }),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["consultations"] }),
  });
}

export function useCompleteConsultation(id: string | undefined) {
  const backend = useBackendAuthenticated();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (force?: boolean) =>
      backend({
        method: "PATCH",
        url: `/api/v1/consultations/${id}/complete${force ? "?force=true" : ""}`,
      }),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["consultation", id] }),
  });
}
